import { useCallback, useEffect, useState } from "react";
import { useSEO } from "@/lib/seo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Pencil, Plus, X, LogOut, UploadCloud } from "lucide-react";

const inputCls = "rounded-sm border border-ink/15 bg-white focus:ring-1 focus:ring-gold focus:border-gold h-10 text-sm w-full px-3 outline-none";
const EMPTY_JOB = { title: "", company: "", location: "", industry: "Manufacturing", qualification: "", experience: "", salary_min: 0, salary_display: "", shift: "Day Shift", employment_type: "Full-time", openings: 1, benefits: [], description: "", gender: "Any", status: "active", deadline: "" };

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("maqline_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-darknavy noise-bg flex items-center justify-center px-5 pt-20" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-sm p-8 border border-gold/25">
        <img src="/maqline-logo.png" alt="Maqline" className="h-8 w-auto mb-6" />
        <h1 className="font-display text-2xl text-ink mb-1">Admin Console</h1>
        <p className="text-xs text-muted mb-6">Vacancy & submission management</p>
        <label className="block text-xs uppercase tracking-wider font-semibold text-ink/70 mb-1.5">Email</label>
        <input data-testid="admin-email" type="email" className={inputCls + " mb-4"} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <label className="block text-xs uppercase tracking-wider font-semibold text-ink/70 mb-1.5">Password</label>
        <input data-testid="admin-password" type="password" className={inputCls + " mb-6"} value={password} onChange={(e) => setPassword(e.target.value)} required />
        <button data-testid="admin-login-submit" disabled={loading} className="btn-sweep w-full bg-navy text-white font-semibold text-sm py-3.5 rounded-sm hover:bg-darknavy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {loading && <Loader2 size={15} className="animate-spin" />} Sign In
        </button>
        <button
          type="button"
          data-testid="admin-request-setup"
          onClick={async () => {
            if (!email) { toast.error("Enter your admin email first"); return; }
            try {
              await api.post("/admin/request-setup", { email });
              toast.success("If this email matches the admin account, a setup link has been sent to the registered owner inbox.");
            } catch (err) { toast.error(formatApiError(err)); }
          }}
          className="w-full text-xs text-muted hover:text-navy transition-colors mt-4 underline underline-offset-2"
        >
          Forgot password? Email me a secure setup link
        </button>
      </form>
    </div>
  );
}

function JobEditor({ job, onClose, onSaved }) {
  const [f, setF] = useState(job || EMPTY_JOB);
  const [loading, setLoading] = useState(false);
  const isEdit = !!job?.id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...f, salary_min: parseInt(f.salary_min, 10) || 0, openings: parseInt(f.openings, 10) || 1, benefits: typeof f.benefits === "string" ? f.benefits.split(",").map((b) => b.trim()).filter(Boolean) : f.benefits };
      if (isEdit) await api.put(`/admin/jobs/${job.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(isEdit ? "Vacancy updated" : "Vacancy created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-darknavy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="job-editor">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-ink">{isEdit ? "Edit Vacancy" : "New Vacancy"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="job-editor-form">
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Job Title *</label><input data-testid="je-title" className={inputCls} value={f.title} onChange={set("title")} required /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Company *</label><input data-testid="je-company" className={inputCls} value={f.company} onChange={set("company")} required /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Location *</label><input className={inputCls} value={f.location} onChange={set("location")} required /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Industry</label><input className={inputCls} value={f.industry} onChange={set("industry")} /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Qualification</label><input className={inputCls} value={f.qualification} onChange={set("qualification")} /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Experience</label><input className={inputCls} value={f.experience} onChange={set("experience")} /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Salary Min (₹/mo)</label><input type="number" className={inputCls} value={f.salary_min} onChange={set("salary_min")} /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Salary Display</label><input className={inputCls} value={f.salary_display} onChange={set("salary_display")} placeholder="₹18,000 – ₹24,000 / month" /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Shift</label>
            <select className={inputCls} value={f.shift} onChange={set("shift")}>{["Day Shift", "Night Shift", "Rotational"].map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Employment Type</label>
            <select className={inputCls} value={f.employment_type} onChange={set("employment_type")}>{["Full-time", "Trainee", "Apprenticeship", "Contract"].map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Openings</label><input type="number" className={inputCls} value={f.openings} onChange={set("openings")} /></div>
          <div><label className="text-xs font-semibold text-ink/70 block mb-1">Status</label>
            <select className={inputCls} value={f.status} onChange={set("status")}><option value="active">Active</option><option value="closed">Closed</option></select></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold text-ink/70 block mb-1">Benefits (comma separated)</label><input className={inputCls} value={Array.isArray(f.benefits) ? f.benefits.join(", ") : f.benefits} onChange={set("benefits")} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold text-ink/70 block mb-1">Description</label><textarea className={inputCls + " h-24 py-2"} value={f.description} onChange={set("description")} /></div>
          <div className="sm:col-span-2">
            <button data-testid="job-editor-save" disabled={loading} className="btn-sweep w-full bg-gold text-darknavy font-bold text-sm py-3.5 rounded-sm hover:bg-lightgold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 size={15} className="animate-spin" />} {isEdit ? "Save Changes" : "Create Vacancy"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CSV_HEADERS = ["title", "company", "location", "industry", "qualification", "experience", "salary_min", "salary_display", "shift", "employment_type", "openings", "benefits", "description", "gender", "deadline"];
const CSV_TEMPLATE = CSV_HEADERS.join(",") + "\n" + '"CNC Operator","ABC Manufacturing Pvt Ltd","Bommasandra, Bengaluru","Automotive","ITI / Diploma","0–2 years",18000,"₹18,000 – ₹24,000 / month","Rotational","Full-time",10,"Canteen facility; Transport support","Operate CNC machines for precision components.","Any","2026-10-31"';

function parseCsv(text) {
  const rows = [];
  let row = [], cur = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(cur); cur = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cur); cur = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else cur += ch;
  }
  if (cur !== "" || row.length) { row.push(cur); if (row.some((c) => c.trim() !== "")) rows.push(row); }
  return rows;
}

function BulkImportModal({ onClose, onDone }) {
  const [parsed, setParsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [rawText, setRawText] = useState("");

  const parse = (raw) => {
    setRawText(raw);
    const rows = parseCsv(raw);
    if (rows.length < 2) { setParsed({ jobs: [], errors: ["No data rows found — include the header row and at least one vacancy."] }); return; }
    const header = rows[0].map((h) => h.trim().toLowerCase());
    const jobs = [], errors = [];
    rows.slice(1).forEach((r, idx) => {
      const obj = {};
      CSV_HEADERS.forEach((h) => {
        const ci = header.indexOf(h);
        if (ci >= 0) obj[h] = (r[ci] || "").trim();
      });
      if (!obj.title || !obj.company || !obj.location) { errors.push(`Row ${idx + 2}: missing title, company or location`); return; }
      obj.salary_min = parseInt(obj.salary_min, 10) || 0;
      obj.openings = parseInt(obj.openings, 10) || 1;
      obj.benefits = obj.benefits ? obj.benefits.split(";").map((b) => b.trim()).filter(Boolean) : [];
      ["industry", "qualification", "experience", "salary_display", "shift", "employment_type", "description", "gender", "deadline"].forEach((k) => { if (!obj[k]) delete obj[k]; });
      jobs.push(obj);
    });
    setParsed({ jobs, errors });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parse(String(reader.result || ""));
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "maqline_vacancies_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const doImport = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/jobs/bulk", { jobs: parsed.jobs });
      setResult(data);
      toast.success(`${data.inserted} vacancies imported`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-darknavy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="bulk-import-modal">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-2xl text-ink">Bulk Import Vacancies</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Close"><X size={20} /></button>
        </div>
        <p className="text-sm text-muted mb-5">Upload a CSV file (or paste CSV text) with your real openings. Required columns: <span className="font-semibold text-ink">title, company, location</span>. Separate multiple benefits with semicolons.</p>

        <button onClick={downloadTemplate} data-testid="bulk-template-download" className="text-xs font-bold text-navy hover:text-gold transition-colors underline underline-offset-4 mb-4">Download CSV template</button>

        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="bulk-file-input" className="block w-full text-sm text-muted file:mr-4 file:py-2.5 file:px-4 file:rounded-sm file:border-0 file:bg-navy file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider hover:file:bg-darknavy file:cursor-pointer mb-3" />
        <textarea
          data-testid="bulk-csv-textarea"
          className="w-full h-36 rounded-sm border border-ink/15 p-3 text-xs font-mono outline-none focus:ring-1 focus:ring-gold"
          placeholder="...or paste CSV content here"
          value={rawText}
          onChange={(e) => parse(e.target.value)}
        />

        {parsed && (
          <div className="mt-4" data-testid="bulk-preview">
            <p className="text-sm"><span className="font-bold text-navy">{parsed.jobs.length}</span> valid rows{parsed.errors.length > 0 && <> · <span className="font-bold text-red-500">{parsed.errors.length}</span> skipped</>}</p>
            {parsed.errors.slice(0, 5).map((er, i) => <p key={i} className="text-xs text-red-500 mt-1">{er}</p>)}
            {parsed.jobs.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto border border-ink/10 rounded-sm">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-muted border-b border-ink/8"><th className="px-3 py-2">Title</th><th className="px-3 py-2">Company</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">Openings</th></tr></thead>
                  <tbody>
                    {parsed.jobs.slice(0, 8).map((j, i) => (
                      <tr key={i} className="border-b border-ink/5"><td className="px-3 py-1.5 font-semibold">{j.title}</td><td className="px-3 py-1.5">{j.company}</td><td className="px-3 py-1.5">{j.location}</td><td className="px-3 py-1.5 font-mono">{j.openings}</td></tr>
                    ))}
                  </tbody>
                </table>
                {parsed.jobs.length > 8 && <p className="text-[11px] text-muted px-3 py-2">+ {parsed.jobs.length - 8} more rows</p>}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="mt-4 bg-gold/10 border border-gold/40 rounded-sm p-4 text-sm" data-testid="bulk-result">
            <p className="font-semibold text-ink">Imported {result.inserted} vacancies.</p>
            {result.failed?.length > 0 && <p className="text-red-500 text-xs mt-1">{result.failed.length} rows failed validation.</p>}
          </div>
        )}

        <button
          onClick={doImport}
          disabled={loading || !parsed || parsed.jobs.length === 0}
          data-testid="bulk-import-submit"
          className="btn-sweep mt-5 w-full bg-gold text-darknavy font-bold text-sm py-3.5 rounded-sm hover:bg-lightgold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          {loading ? "Importing..." : `Import ${parsed?.jobs.length || 0} Vacancies`}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  useSEO("Admin | Maqline Solutions", "Maqline admin console.");
  const [authed, setAuthed] = useState(!!localStorage.getItem("maqline_admin_token"));
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [subs, setSubs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/jobs");
      setJobs(data.jobs);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("maqline_admin_token"); setAuthed(false); }
    }
  }, []);

  const loadSubs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/submissions");
      setSubs(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (authed) { loadJobs(); loadSubs(); }
  }, [authed, loadJobs, loadSubs]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const del = async (id) => {
    if (!window.confirm("Delete this vacancy?")) return;
    await api.delete(`/admin/jobs/${id}`);
    toast.success("Vacancy deleted");
    loadJobs();
  };

  return (
    <div className="min-h-screen bg-steel pt-28 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl text-ink">Admin Console</h1>
            <p className="text-sm text-muted">Manage vacancies and view submissions</p>
          </div>
          <button onClick={() => { localStorage.removeItem("maqline_admin_token"); setAuthed(false); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-ink transition-colors"><LogOut size={15} /> Sign out</button>
        </div>

        <div className="flex gap-2 mb-6">
          {["jobs", "submissions"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`text-xs font-bold uppercase tracking-[0.12em] px-5 py-3 rounded-sm border transition-colors ${tab === t ? "bg-gold text-darknavy border-gold" : "bg-white text-ink/60 border-ink/15 hover:border-gold"}`}>
              {t === "jobs" ? `Vacancies (${jobs.length})` : "Submissions"}
            </button>
          ))}
        </div>

        {tab === "jobs" && (
          <div className="bg-white border border-ink/10 rounded-sm overflow-hidden" data-testid="admin-jobs-panel">
            <div className="flex items-center justify-between p-5 border-b border-ink/8">
              <p className="font-semibold text-ink text-sm">{jobs.length} vacancies</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowBulk(true)} data-testid="admin-bulk-import" className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors"><UploadCloud size={14} /> Bulk Import CSV</button>
                <button onClick={() => { setEditing(null); setShowEditor(true); }} data-testid="admin-add-job" className="btn-sweep inline-flex items-center gap-2 bg-gold text-darknavy text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm hover:bg-lightgold transition-colors"><Plus size={14} /> Add Vacancy</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted border-b border-ink/8">
                    <th className="px-5 py-3">Title</th><th className="px-5 py-3">Company</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">Openings</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="border-b border-ink/5 hover:bg-steel/60 transition-colors" data-testid={`admin-job-row-${j.id.slice(0, 8)}`}>
                      <td className="px-5 py-3.5 font-semibold text-ink">{j.title}</td>
                      <td className="px-5 py-3.5 text-muted">{j.company}</td>
                      <td className="px-5 py-3.5 text-muted">{j.location}</td>
                      <td className="px-5 py-3.5 font-mono font-bold text-navy">{j.openings}</td>
                      <td className="px-5 py-3.5"><span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-sm ${j.status === "active" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{j.status}</span></td>
                      <td className="px-5 py-3.5 text-right">
                        <button onClick={() => { setEditing(j); setShowEditor(true); }} data-testid={`admin-edit-${j.id.slice(0, 8)}`} className="text-navy hover:text-gold p-1.5 transition-colors" aria-label="Edit"><Pencil size={15} /></button>
                        <button onClick={() => del(j.id)} data-testid={`admin-delete-${j.id.slice(0, 8)}`} className="text-red-400 hover:text-red-600 p-1.5 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "submissions" && subs && (
          <div className="space-y-6" data-testid="admin-submissions-panel">
            {Object.entries(subs).map(([key, group]) => (
              <div key={key} className="bg-white border border-ink/10 rounded-sm overflow-hidden">
                <div className="p-5 border-b border-ink/8 flex items-center justify-between">
                  <p className="font-semibold text-ink text-sm">{group.label}</p>
                  <span className="font-mono text-sm font-bold text-gold">{group.count}</span>
                </div>
                {group.items.length === 0 ? (
                  <p className="px-5 py-6 text-sm text-muted">No submissions yet.</p>
                ) : (
                  <div className="divide-y divide-ink/5">
                    {group.items.slice(0, 10).map((it) => (
                      <div key={it.id} className="px-5 py-4 text-sm">
                        <p className="font-semibold text-ink">{it.full_name || it.company_name || it.college_name || it.name} <span className="text-muted font-normal">· {it.phone || it.business_email || it.email}</span></p>
                        <p className="text-xs text-muted mt-1 truncate">{it.job_titles?.join(" · ") || it.job_role || it.program || it.requirement_type || it.subject || it.qualification}</p>
                        {it.resume_path && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.resume_path}`} target="_blank" rel="noreferrer" data-testid={`resume-link-${it.id.slice(0, 8)}`} className="inline-block text-xs font-bold text-navy hover:text-gold mt-1 transition-colors">View resume ↗</a>
                        )}
                        <p className="text-[11px] text-muted/70 mt-0.5">{new Date(it.created_at).toLocaleString("en-IN")}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {showEditor && <JobEditor job={editing} onClose={() => setShowEditor(false)} onSaved={loadJobs} />}
      {showBulk && <BulkImportModal onClose={() => setShowBulk(false)} onDone={loadJobs} />}
    </div>
  );
}
